import { create } from 'zustand';
import apiClient from '../services/axiosInterceptor';
import { useSchoolDetailsStore } from '../hooks/useSchoolDetailsStore';

interface ClassOption {
  label: string;
  value: string;
  id: string;
  sections: string[];
}

interface ClassResponse {
  id: string;
  class_name: string;
  class_order: number;
  sections: string[];
}

interface ClassesStore {
  classOptions: ClassOption[];
  fetchClasses: (includePlaceholder?: boolean) => Promise<void>;
  hasFetched: boolean;
  isFetching: boolean;
  lastSessionId: string | null;
  reset: () => void;
}

const useClassesStore = create<ClassesStore>((set, get) => ({
  classOptions: [],
  hasFetched: false,
  isFetching: false,
  lastSessionId: null,

  reset: () => {
    set({
      classOptions: [],
      hasFetched: false,
      isFetching: false,
      lastSessionId: null,
    });
  },

  fetchClasses: async (includePlaceholder = false) => {
    const { isFetching, hasFetched, classOptions, lastSessionId } = get();

    const sessionId =
      useSchoolDetailsStore.getState().schoolDetails?.current_session?.id || null;

    if (
      isFetching ||
      (hasFetched && lastSessionId === sessionId && classOptions.length > 0)
    ) {
      set({ hasFetched: true });
      return;
    }

    set({ isFetching: true });

    try {
      let url = `classes?page=1&limit=25`;
      if (sessionId) {
        url += `&session_id=${sessionId}`;
      }

      const response = await apiClient.get(url);

      if (response.data.status === 'success') {
        if (response.data.classes.length === 0) {
          const emptyOption = includePlaceholder
            ? [{ label: 'No classes available', value: '', id: '', sections: [] }]
            : [];
          set({
            classOptions: emptyOption,
            hasFetched: true,
            lastSessionId: sessionId,
          });
        } else {
          const sortedClasses = response.data.classes
            .sort((a: ClassResponse, b: ClassResponse) => a.class_order - b.class_order)
            .map((cls: ClassResponse) => ({
              label: cls.class_name,
              value: cls.class_name,
              id: cls.id,
              sections: cls.sections,
            }));

          const options = includePlaceholder
            ? [{ label: 'Select a Class', value: '', id: '', sections: [] }, ...sortedClasses]
            : sortedClasses;

          set({
            classOptions: options,
            hasFetched: true,
            lastSessionId: sessionId,
          });
        }
      } else if (response.data.message === 'empty') {
        const emptyOption = includePlaceholder
          ? [{ label: 'No classes available', value: '', id: '', sections: [] }]
          : [];
        set({
          classOptions: emptyOption,
          hasFetched: true,
          lastSessionId: sessionId,
        });
      }
    } catch (error: any) {
    } finally {
      set({ isFetching: false, hasFetched: true });
    }
  },
}));

export default useClassesStore;